import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, Card, CardBody, CardHeader, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import PanelHeader from "components/PanelHeader/PanelHeader.js";
import { BASE_URL } from "utils/constants";

const today = new Date().toISOString().split("T")[0];

const defaultFromDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().split("T")[0];
};

const statusClassName = (status) => {
  switch (`${status}`) {
    case "0": return "badge bg-danger text-white";
    case "1": return "badge bg-success text-white";
    case "2": return "badge bg-info text-dark";
    case "3": return "badge bg-warning text-dark";
    case "4": return "badge bg-secondary text-white";
    default: return "badge bg-light text-dark";
  }
};

function DailyAttendance() {
  const [attendanceType, setAttendanceType] = useState("onelecture");
  const [fromDate, setFromDate] = useState(defaultFromDate());
  const [toDate, setToDate] = useState(today);
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal Breakdown state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDayLectures, setSelectedDayLectures] = useState([]);
  const [selectedDayDate, setSelectedDayDate] = useState("");

  const openBreakdown = (record) => {
    setSelectedDayDate(record.attendance_date || "");
    setSelectedDayLectures(record.sessions || []);
    setModalOpen(true);
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`${BASE_URL}/student/getdailyattendance.php`, {
      params: { from: fromDate, to: toDate, type: attendanceType },
    }).then((response) => {
      setSummary(response.data.data.summary);
      setRecords(response.data.data.records || []);
    }).catch((error) => {
      setSummary(null);
      setRecords([]);
      Swal.fire(error.response?.data?.msg || "Unable to load daily attendance", "", "error");
    }).finally(() => setLoading(false));
  }, [fromDate, toDate, attendanceType]);

  const isOneLecture = attendanceType === "onelecture";

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content">
        <Card>
          <CardHeader><h4 className="title">Daily Attendance</h4></CardHeader>
          <CardBody>
            <Row className="mb-3">
              <Col md={4} sm={6} className="mb-2">
                <label htmlFor="attendance-type">Attendance Type</label>
                <select
                  id="attendance-type"
                  className="form-control"
                  value={attendanceType}
                  onChange={(event) => setAttendanceType(event.target.value)}
                >
                  <option value="onelecture">Attendance at least one lecture</option>
                  <option value="gate">Daily / Gate Attendance</option>
                </select>
              </Col>
              <Col md={4} sm={6} className="mb-2">
                <label htmlFor="attendance-from">From</label>
                <input id="attendance-from" className="form-control" type="date"
                  value={fromDate} max={toDate}
                  onChange={(event) => setFromDate(event.target.value)} />
              </Col>
              <Col md={4} sm={6} className="mb-2">
                <label htmlFor="attendance-to">To</label>
                <input id="attendance-to" className="form-control" type="date"
                  value={toDate} min={fromDate}
                  onChange={(event) => setToDate(event.target.value)} />
              </Col>
            </Row>

            {loading ? (
              <div className="text-center py-5"><Spinner color="info" /></div>
            ) : (
              <>
                {summary && (
                  <Row className="mb-3">
                    {(isOneLecture
                      ? [
                          ["Total Lectures", summary.total],
                          ["Attended Lectures", summary.attended],
                          ["Absent Lectures", summary.absent],
                          ["Late Lectures", summary.late],
                          ["Excused Lectures", summary.excused],
                        ]
                      : [
                          ["Total", summary.total],
                          ["Attended", summary.attended],
                          ["Absent", summary.absent],
                          ["Late", summary.late],
                          ["Excused", summary.excused],
                        ]
                    ).map(([label, value]) => (
                      <Col lg md={4} xs={6} className="mb-2" key={label}>
                        <div className="border rounded text-center p-2 h-100">
                          <div className="font-weight-bold" style={{ fontSize: "20px" }}>{value ?? 0}</div>
                          <small>{label}</small>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}

                <div className="table-responsive">
                  <table className="table table-striped table-bordered mb-0">
                    <thead>
                      {isOneLecture ? (
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Lectures Attended</th>
                          <th>Excuse</th>
                        </tr>
                      ) : (
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Entry</th>
                          <th>Leave</th>
                          <th>Guardian</th>
                          <th>Excuse</th>
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {records.map((record) => (
                        <tr key={record.id}>
                          <td>{record.attendance_date}</td>
                          <td><span className={statusClassName(record.status)}>{record.status_label}</span></td>
                          {isOneLecture ? (
                            <>
                              <td>
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="font-weight-bold">{record.lectures_summary || "-"}</span>
                                  {record.sessions && record.sessions.length > 0 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-info py-0 px-2 ml-2"
                                      style={{ fontSize: "11px", whiteSpace: "nowrap" }}
                                      onClick={() => openBreakdown(record)}
                                    >
                                      View Breakdown
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td>{record.excuse || (record.excuse_ids
                                ? `${record.excuse_status || ""}${record.excuse_description ? ` - ${record.excuse_description}` : ""}`
                                : "-")}</td>
                            </>
                          ) : (
                            <>
                              <td>{record.entrytime || "-"}</td>
                              <td>{record.leavetime || "-"}</td>
                              <td>{record.guardianname?.trim() || record.otherguardian || "-"}</td>
                              <td>{record.excuse || (record.excuse_ids
                                ? `${record.excuse_status || ""}${record.excuse_description ? ` - ${record.excuse_description}` : ""}`
                                : "-")}</td>
                            </>
                          )}
                        </tr>
                      ))}
                      {!records.length && (
                        <tr>
                          <td colSpan={isOneLecture ? 4 : 6} className="text-center py-4">
                            No attendance records in this period.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Modal for minimal Lecture Breakdown */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="md" centered>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          Lectures Breakdown — {selectedDayDate}
        </ModalHeader>
        <ModalBody>
          {selectedDayLectures.length === 0 ? (
            <div className="text-center py-3 text-muted">
              No lecture details recorded for this day.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-striped table-bordered mb-0 align-middle">
                <thead className="thead-light">
                  <tr>
                    <th>Period</th>
                    <th>Course Name</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDayLectures.map((session, idx) => (
                    <tr key={session.id || idx}>
                      <td className="font-weight-bold">{session.period || "-"}</td>
                      <td>{session.course_name || "-"}</td>
                      <td>
                        <span className={statusClassName(session.status)}>
                          {session.status_label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default DailyAttendance;
